import classnames from 'classnames';
import type { SkillTreeDialModel } from './skill-tree-model';
import type { SkillBranch } from '@sorrem/shared';
import { SKILL_TREE_DIAL_SIZE } from './skill-tree-layout';
import { toDialSelectionId, type SkillTreeSvgIds } from './skill-tree-ids';
import type { SkillTreeIntroSchedule } from './skill-tree-intro';
import {
    DIAL_ARC_RADIUS,
    DIAL_PIECE_SCALE,
    DIAL_START_ANGLE,
    DIAL_SWEEP,
    PIECE_BOX,
    PIECE_CENTER,
    resolveDialStudPoint,
    toPieceUrl,
    buildWedgePath,
} from './skill-tree-pieces';
import { SkillTreeIntroPart } from './SkillTreeIntroPart';
import { SkillTreeIntroSweep } from './SkillTreeIntroSweep';
import styles from './SkillTreeScene.module.scss';

interface SkillTreeDialProps {
    readonly dial: SkillTreeDialModel;
    readonly isHovered: boolean;
    readonly isSelected: boolean;
    /** The scene's SVG def ids (the label shadow, the dial's clip and intro sweep). */
    readonly svgIds: SkillTreeSvgIds;
    /** The Skills tab intro while it plays: the dial pops in as its link arrives, then its values sweep in (or all of it back out). */
    readonly intro: SkillTreeIntroSchedule | undefined;
}

const HALF = SKILL_TREE_DIAL_SIZE / 2;

/** Percent text color: each branch's light tone. */
const PERCENT_COLORS: Record<SkillBranch, string> = {
    martial: '#F0CF7A',
    essence: '#DCCBFA',
    crafting: '#E6EAF0',
};

/** How a dial looks at 100%: its mastered piece, and whether that piece replaces the gauge or frames it. */
interface MasteredDialLook {
    readonly piecePath: string;
    /** Martial and Essence replace the gauge with their own mastered piece; Crafting keeps it under its border. */
    readonly replacesGauge: boolean;
    /** Essence's studs turn to diamonds. */
    readonly hasDiamondStuds: boolean;
}

const MASTERED_DIAL_LOOKS: Record<SkillBranch, MasteredDialLook> = {
    martial: { piecePath: 'dial/mastered-martial.svg', replacesGauge: true, hasDiamondStuds: false },
    essence: { piecePath: 'dial/mastered-essence.svg', replacesGauge: true, hasDiamondStuds: true },
    crafting: { piecePath: 'dial/mastered-crafting-border.svg', replacesGauge: false, hasDiamondStuds: false },
};

function Studs({ dial, hasDiamonds }: { readonly dial: SkillTreeDialModel; readonly hasDiamonds: boolean }): JSX.Element {
    const { branch } = dial.placement;
    return (
        <>
            {dial.studs.map((stud) => {
                const point = resolveDialStudPoint(stud.percent);
                if (hasDiamonds) {
                    return <image key={stud.percent} href={toPieceUrl('dial/stud-diamond-essence.svg')} x={point.x - 8} y={point.y - 8} width={16} height={16} />;
                }
                const path = stud.isReached ? `dial/stud-lit-${branch}.svg` : `dial/stud-socket-${branch}.svg`;
                return <image key={stud.percent} href={toPieceUrl(path)} x={point.x - 6} y={point.y - 6} width={12} height={12} />;
            })}
        </>
    );
}

/**
 * A branch's mastery dial: a 300-degree gauge with milestone studs and the
 * percentage at its center, assembled from the shared dial pieces. At 100%
 * it borrows its branch's mastered look.
 */
export function SkillTreeDial({ dial, isHovered, isSelected, svgIds, intro }: SkillTreeDialProps): JSX.Element {
    const { branch, position, name } = dial.placement;
    const isMastered = !dial.isUnawakened && dial.percent >= 100;
    const masteredLook = MASTERED_DIAL_LOOKS[branch];
    const clipId = svgIds.dialClip(branch);
    const fillEnd = DIAL_START_ANGLE + DIAL_SWEEP * (Math.min(100, dial.percent) / 100);
    const sweepId = svgIds.dialSweep(branch);
    const replacesGauge = isMastered && masteredLook.replacesGauge;
    const introTiming = intro?.dials.get(branch);

    return (
        <SkillTreeIntroPart window={introTiming?.pop} motion="pop" origin={position}>
            <g className={classnames(styles.dial, dial.isUnawakened && styles.unawakened)}>
                <g transform={`translate(${position.x - HALF} ${position.y - HALF}) scale(${DIAL_PIECE_SCALE})`}>
                    {introTiming && (
                        <SkillTreeIntroSweep
                            id={sweepId}
                            center={PIECE_CENTER}
                            radius={DIAL_ARC_RADIUS}
                            width={44}
                            startAngle={DIAL_START_ANGLE}
                            window={introTiming.values}
                        />
                    )}
                    {/* The unlit track stays put; the values on it sweep in during the intro. */}
                    {!replacesGauge && <image href={toPieceUrl('dial/track.svg')} width={PIECE_BOX} height={PIECE_BOX} />}
                    <g mask={introTiming ? `url(#${sweepId})` : undefined}>
                        {replacesGauge && <image href={toPieceUrl(masteredLook.piecePath)} x={-12} y={-12} width={144} height={144} />}
                        {!replacesGauge && dial.percent > 0 && (
                            <>
                                <clipPath id={clipId}>
                                    <path d={buildWedgePath(PIECE_CENTER, 70, DIAL_START_ANGLE, fillEnd)} />
                                </clipPath>
                                <g clipPath={`url(#${clipId})`}>
                                    <image href={toPieceUrl(`dial/glow-${branch}.svg`)} width={PIECE_BOX} height={PIECE_BOX} />
                                    <image href={toPieceUrl(`dial/fill-${branch}.svg`)} width={PIECE_BOX} height={PIECE_BOX} />
                                </g>
                            </>
                        )}
                        {isMastered && !masteredLook.replacesGauge && (
                            <image href={toPieceUrl(masteredLook.piecePath)} x={-12} y={-12} width={144} height={144} />
                        )}
                        <Studs dial={dial} hasDiamonds={isMastered && masteredLook.hasDiamondStuds} />
                    </g>
                    {/* TODO(#139): the painted branch emblem goes above the percent. */}
                    <text className={styles.dialPercent} x={PIECE_CENTER.x} y={PIECE_CENTER.y + 6} fill={PERCENT_COLORS[branch]}>
                        {Math.round(dial.percent)}%
                    </text>
                    {isHovered && !isSelected && <image href={toPieceUrl(`${branch}/halo-hover.svg`)} x={-4} y={-4} width={128} height={128} />}
                    {isSelected && <image href={toPieceUrl(`${branch}/rings-selected.svg`)} x={-6} y={-6} width={132} height={132} />}
                </g>
                <text
                    className={styles.dialLabel}
                    x={position.x}
                    y={position.y + HALF + 36}
                    fill={PERCENT_COLORS[branch]}
                    filter={`url(#${svgIds.textShadow})`}
                >
                    {name}
                </text>
                <circle className={styles.hitTarget} cx={position.x} cy={position.y} r={HALF} data-node-id={toDialSelectionId(branch)} />
            </g>
        </SkillTreeIntroPart>
    );
}
